import { Form, Formik } from "formik";
import { At, CheckCircle, Spinner, XCircle } from "@phosphor-icons/react";
import { FC } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import useCheckUsernameAvailability from "../hooks/useCheckUsernameAvailability";
import useUpdateAccount from "../hooks/useUpdateAccount";

const schema = yup.object().shape({
  username: yup.string().required("Username is required"),
});

const ChangeUsername: FC<{
  setShowUsernameModal: React.Dispatch<React.SetStateAction<boolean>>;
  /** Runs after a successful save, e.g. to return to a setup checklist. */
  onUpdated?: () => void;
}> = ({ setShowUsernameModal, onUpdated }) => {
  const { refetch } = useAuth();

  const {
    checkUsername,
    checkingUsername,
    checkingUsernameError,
    isCheckUsernameError,
    checkUsernameData,
  } = useCheckUsernameAvailability();
  const {
    updateAccount,
    updateAccountError,
    isUpdatingAccountError,
    updatingAccount,
  } = useUpdateAccount({
    async onSuccess() {
      // Fresh profile first, so a returning setup gate sees the username.
      await refetch();
      setShowUsernameModal(false);
      onUpdated?.();
    },
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5 z-50"
    >
      <Formik
        validationSchema={schema}
        initialValues={{ username: "" }}
        onSubmit={(values, { resetForm }) => {
          updateAccount(values, {
            onSuccess() {
              resetForm();
            },
          });
        }}
      >
        {({ touched, errors, handleBlur, handleChange }) => {
          return (
            <Form className="bg-white max-w-sm w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-2">
              <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
                <At size={28} />
              </div>
              <div className="flex items-center flex-col">
                <div className="font-medium">Change Username</div>
                <p className="text-gray-500 text-center">
                  Enter your new username.
                </p>
              </div>
              <br />
              <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                <div className="p-2">
                  <At className="text-gray-500 size-6" weight="bold" />
                </div>
                <input
                  onBlur={handleBlur("username")}
                  onChange={(e) => {
                    checkUsername({ username: e.target.value });
                    handleChange("username")(e.target.value);
                  }}
                  type="text"
                  id="username"
                  name="username"
                  placeholder="Choose Username"
                  className="px-2 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                />
                <div className="p-2">
                  {checkingUsername ? (
                    <Spinner className="text-gray-500 size-6 animate-spin" />
                  ) : (
                    checkUsernameData &&
                    (checkUsernameData?.status ? (
                      <CheckCircle
                        className="text-green-500 size-6"
                        weight="fill"
                      />
                    ) : (
                      <XCircle className="text-red-500 size-6" weight="fill" />
                    ))
                  )}
                </div>
              </div>
              <div className="text-green-500 w-full">
                {checkUsernameData?.status
                  ? checkUsernameData?.message
                  : undefined}
              </div>
              <div className="text-red-500 w-full">
                {isCheckUsernameError
                  ? checkingUsernameError?.message
                  : touched.username
                  ? errors.username
                  : undefined}
              </div>
              {isUpdatingAccountError && (
                <div className="text-red-500 text-left flex w-full">
                  {updateAccountError?.message}
                </div>
              )}
              <div className="flex items-center gap-5 w-full">
                <button
                  type="button"
                  onClick={() => setShowUsernameModal(false)}
                  className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
                >
                  {updatingAccount || checkingUsername ? "Loading" : "Change"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </motion.div>
  );
};

export default ChangeUsername;
